import axios from "axios";
import { useCallback, useEffect, useState } from "react";

export const BASE = process.env.REACT_APP_BACKEND_URL;
export const api = axios.create({ baseURL: `${BASE}/api`, withCredentials: true });

let refreshing = null;
api.interceptors.response.use(
  (r) => r,
  async (err) => {
    const cfg = err.config || {};
    if (err.response?.status === 401 && !cfg._retry && !String(cfg.url).includes("/auth/")) {
      cfg._retry = true;
      try {
        refreshing = refreshing || api.post("/auth/refresh");
        await refreshing;
        refreshing = null;
        return api(cfg);
      } catch (e) {
        refreshing = null;
      }
    }
    return Promise.reject(err);
  }
);

export function errMsg(e) {
  const d = e?.response?.data?.detail;
  if (!d) return e?.message || "Es ist ein Fehler aufgetreten.";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => `${(x.loc || []).slice(-1)[0] || ""}: ${x.msg}`).join(" · ");
  return String(d.msg || JSON.stringify(d));
}

export const fileUrl = (p) => (!p ? "" : p.startsWith("http") ? p : BASE + p);
export const eur = (n) => (n == null || n === "" ? "" : new Intl.NumberFormat("de-AT", { style: "currency", currency: "EUR" }).format(n));
export const uid = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : String(Date.now()) + Math.random().toString(16).slice(2));

export function fmtLocal(s, withTime = true) {
  if (!s) return "";
  const [d, t] = s.split("T");
  const [y, m, day] = d.split("-");
  return `${Number(day)}.${Number(m)}.${y}${withTime && t ? `, ${t.slice(0, 5)} Uhr` : ""}`;
}

export function waLink(site, area, text) {
  const who = site?.whatsapp_areas?.[area] || "operator";
  const num = (site?.whatsapp?.[who] || site?.whatsapp?.operator || "").replace(/\D/g, "");
  return num ? `https://wa.me/${num}?text=${encodeURIComponent(text)}` : null;
}

export function mapLink(site, v) {
  const q = [v?.name, v?.address, v?.postal_code, v?.city].filter(Boolean).join(", ");
  if (!q || site?.map_provider === "none") return null;
  if (v?.map_url) return v.map_url;
  return site?.map_provider === "google" ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}` : `https://www.openstreetmap.org/search?query=${encodeURIComponent(q)}`;
}

export function useApi(path, deps = []) {
  const [state, set] = useState({ data: null, loading: true, error: null });
  const load = useCallback(async () => {
    if (!path) return;
    set((s) => ({ ...s, loading: true }));
    try {
      const r = await api.get(path);
      set({ data: r.data, loading: false, error: null });
    } catch (e) {
      set({ data: null, loading: false, error: errMsg(e), status: e?.response?.status });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, ...deps]);
  useEffect(() => {
    load();
  }, [load]);
  return { ...state, reload: load };
}

export function useSeo(title, description, jsonLd) {
  useEffect(() => {
    document.title = title ? `${title} – Kunst und Fund` : "Kunst und Fund";
    let m = document.querySelector('meta[name="description"]');
    if (!m) {
      m = document.createElement("meta");
      m.name = "description";
      document.head.appendChild(m);
    }
    m.content = description || "Verteilte Galerie: Kunst in Lokalen, Prints, Originale und restaurierte Fundstücke.";
    ["og:title", "og:description"].forEach((p, i) => {
      let t = document.querySelector(`meta[property="${p}"]`);
      if (!t) {
        t = document.createElement("meta");
        t.setAttribute("property", p);
        document.head.appendChild(t);
      }
      t.content = i === 0 ? document.title : m.content;
    });
    let s = document.getElementById("jsonld");
    if (s) s.remove();
    if (jsonLd) {
      s = document.createElement("script");
      s.type = "application/ld+json";
      s.id = "jsonld";
      s.textContent = JSON.stringify(jsonLd);
      document.head.appendChild(s);
    }
  }, [title, description, jsonLd]);
}

export async function download(path, filename) {
  const r = await api.get(path, { responseType: "blob" });
  const url = URL.createObjectURL(r.data);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
